<template>
  <v-menu
    ref="menu"
    v-model="menu"
    :close-on-content-click="false"
    :return-value.sync="innerDate"
    transition="scale-transition"
    offset-y
    z-index="500"
    max-width="300"
    content-class="ui-date-picker-menu"
  >
    <template v-slot:activator="props">
      <slot
        name="activator"
        v-bind="{
          ...props,
          inputValue: formattedValue,
        }"
      />
    </template>

    <v-date-picker
      v-model="innerDate"
      type="date"
      locale="en-EN"
      :picker-date.sync="pickerDate"
      v-bind="datePickerSettings"
      @keydown.native.enter="onEnterKeydown"
    >
      <v-container fluid class="py-3 px-1">
        <v-row v-if="showTodayPlus3Months">
          <v-col class="py-0">
            <ui-button variant="secondary" block class="w-100" size="small" @click="presetBtn('todayPlus3Months')">
              {{ $t('base.presets.todayPlus3Months') }}
            </ui-button>
          </v-col>
        </v-row>
        <v-row v-if="showShortcuts">
          <v-col
            v-for="(presetId, index) in shortcutPresetIds"
            :key="presetId"
            cols="6"
            class="pt-0 pb-2"
            :class="index % 2 ? 'pl-1' : 'pr-1'"
          >
            <ui-button variant="secondary" block class="w-100" size="small" @click="presetBtn(presetId)">
              {{ $t(`base.presets.${presetId}`) }}
            </ui-button>
          </v-col>
          <v-col cols="12" class="pt-0 pb-1">
            <ui-select
              :options="itemsPeriod"
              size="small"
              :label-name="$t('base.choosePeriod')"
              class="date-period-select"
              @input="onSelectPreset"
            />
          </v-col>
        </v-row>

        <v-row v-if="showTime">
          <v-col cols="6" class="pb-1 pr-1 pt-1">
            <ui-time-picker v-model="innerTime.start">
              <template #activator="props">
                <ui-text-field :value="props.inputValue" dense readonly v-on="props.on">
                  <template #prepend-inner>
                    <v-icon color="black lighten-2" size="large" style="margin-top: 2px"
                      >mdi-clock-time-five-outline</v-icon
                    >
                  </template>
                </ui-text-field>
              </template>
            </ui-time-picker>
          </v-col>

          <v-col cols="6" class="pb-1 pl-1 pt-1">
            <ui-time-picker v-model="innerTime.end">
              <template #activator="props">
                <ui-text-field :value="props.inputValue" dense readonly v-on="props.on">
                  <template #prepend-inner>
                    <v-icon color="black lighten-2" size="large" style="margin-top: 2px"
                      >mdi-clock-time-five-outline</v-icon
                    >
                  </template>
                </ui-text-field>
              </template>
            </ui-time-picker>
          </v-col>
        </v-row>

        <v-row>
          <v-col :cols="showReset ? 4 : 6" class="pr-1">
            <ui-button variant="secondary" block class="w-100" size="small" @click="menu = false">
              {{ $t('base.cancel') }}
            </ui-button>
          </v-col>
          <v-col v-if="showReset" cols="4" class="px-1">
            <ui-button variant="secondary" block class="w-100" size="small" @click="onReset">
              {{ $t('base.reset') }}
            </ui-button>
          </v-col>
          <v-col :cols="showReset ? 4 : 6" class="pl-1">
            <ui-button block class="w-100" size="small" @click="onApply">
              {{ $t('base.apply') }}
            </ui-button>
          </v-col>
        </v-row>
      </v-container>
    </v-date-picker>
  </v-menu>
</template>

<script>
import {
  DATE_FULL_MONTH_FORMAT,
  DATE_TIME_FORMAT,
  TIME_SHORT_FORMAT,
  DATE_FORM_FORMAT,
  DATE_MONTH_FULL_FORMAT,
} from '@/constants/date'
import isFunction from 'lodash/isFunction'
import orderBy from 'lodash/orderBy'
import { DATE_PRESETS } from '@/components/ui/UIDatePickerField/config'
import UiTimePicker from '@/components/ui/UIDatePickerField/UiTimePicker.vue'

export default {
  name: 'ui-date-range-picker',
  components: { UiTimePicker },
  props: {
    value: {
      type: [Object, String],
      default: () => ({
        start: '',
        end: '',
      }),
    },
    showReset: {
      type: Boolean,
      default: false,
    },
    customLabel: {
      type: String,
      default: null,
    },
    showShortcuts: {
      type: Boolean,
      default: false,
    },
    showTodayPlus3Months: {
      type: Boolean,
      default: false,
    },
    showTime: {
      type: Boolean,
      default: false,
    },
    dateRangeString: {
      type: Boolean,
      default: false,
    },
    settings: {
      type: Object,
      default: () => ({}),
    },
    displayDateFormatter: {
      type: Function,
      default: null,
    },
    returnValueFormat: {
      type: String,
      default: DATE_TIME_FORMAT,
    },
  },
  data() {
    return {
      menu: false,
      innerDate: ['', ''],
      innerTime: {
        start: '00:00',
        end: '23:59',
      },
      itemsPeriod: [...DATE_PRESETS],
      // Pairs per row: left column — current period, right column — previous one
      shortcutPresetIds: ['today', 'yesterday', 'currentMonth', 'previousMonth', 'currentYear', 'previousYear'],
      pickerDate: '',
      lastValueSignature: null,
    }
  },
  computed: {
    isEmptyDate() {
      return !this.innerDate.length || (!this.innerDate[0] && !this.innerDate[1])
    },
    isSingleDate() {
      return this.innerDate.length && (!this.innerDate[0] || !this.innerDate[1])
    },
    formattedValue() {
      if (this.isEmptyDate) {
        return this.customLabel || this.$t('base.selectDate')
      }

      const [start, end] = this.orderedInnerDate

      if (isFunction(this.displayDateFormatter)) {
        return this.displayDateFormatter({
          start,
          end,
        })
      }
      const isSameYear = this.$moment(start).isSame(this.$moment(end), 'year')
      return `${this.$moment(start).format(
        isSameYear ? DATE_MONTH_FULL_FORMAT : DATE_FULL_MONTH_FORMAT,
      )} - ${this.$moment(end).format(DATE_FULL_MONTH_FORMAT)}`
    },
    headerText() {
      if (this.isEmptyDate) {
        return ''
      }

      const [start, end] = this.orderedInnerDate

      return `${this.$moment(start).format('DD MMM')} - ${this.$moment(end).format('DD MMM')}`
    },
    datePickerSettings() {
      const defaultSettings = {
        showCurrent: true,
        flat: true,
        color: 'blue500',
        scrollable: true,
        width: '300',
        firstDayOfWeek: 1,
        locale: this.$i18n.locale,
        selectedItemsText: this.headerText,
      }

      return {
        ...defaultSettings,
        ...this.settings,
        range: true,
      }
    },
    orderedInnerDate() {
      return this.orderRangeData(this.innerDate)
    },
  },
  watch: {
    value: {
      handler(dates) {
        if (this.dateRangeString) {
          this.innerDate = dates ? dates.split('|') : ['', '']
          return
        }
        // Callers like ui-date-range-picker(:value="{start: item.dateFrom, end: item.dateTo}")
        // pass a brand new object on every parent re-render, even when start/end didn't
        // actually change. Compare against the last processed content instead of the object
        // reference so an unrelated re-render mid-selection doesn't wipe innerDate.
        const signature = dates ? `${dates.start || ''}|${dates.end || ''}` : ''
        if (signature === this.lastValueSignature) return
        this.lastValueSignature = signature

        if (dates && dates.start && dates.end) {
          this.innerDate = [
            this.$moment(dates.start).format(DATE_FORM_FORMAT),
            this.$moment(dates.end).format(DATE_FORM_FORMAT),
          ]
          this.pickerDate = this.innerDate[1]
          if (this.showTime) {
            this.innerTime.start = this.$moment(dates.start).format(TIME_SHORT_FORMAT)
            this.innerTime.end = this.$moment(dates.end).format(TIME_SHORT_FORMAT)
          }
        } else {
          this.innerDate = ['', '']
        }
      },
      immediate: true,
      deep: true,
    },
    menu: {
      handler(newVal) {
        if (!newVal) {
          this.$emit('close-picker')
        }
      },
    },
    innerDate: {
      handler(newVal) {
        if (newVal.length === 1) {
          this.$emit('first-date', newVal)
        } else if (newVal[0] !== newVal[1]) {
          this.$emit('both-dates', newVal)
          this.$emit('second-date', newVal)
        } else {
          this.$emit('second-date', newVal)
        }
      },
    },
  },
  methods: {
    onEnterKeydown(event) {
      if (event.target.closest('.ui-btn-width, .multiselect')) {
        return
      }

      event.preventDefault()
      this.onApply()
    },
    presetBtn(presetId) {
      const preset = DATE_PRESETS.find(preset => preset.id === presetId)
      this.onSelectPreset(preset)
      this.onApply()
    },
    onSelectPreset(preset) {
      this.innerDate = [
        this.$moment(preset.dates.start).format(DATE_FORM_FORMAT),
        this.$moment(preset.dates.end).format(DATE_FORM_FORMAT),
      ]
      this.innerTime.start = this.$moment(preset.dates.start).format(TIME_SHORT_FORMAT)
      this.innerTime.end = this.$moment(preset.dates.end).format(TIME_SHORT_FORMAT)
    },
    orderRangeData(dates, order = 'asc') {
      return orderBy(
        dates,
        val => {
          return this.$moment(val).format('YYYYMMDD')
        },
        [order],
      )
    },
    onReset() {
      this.$emit('reset')
    },
    onApply() {
      this.$refs.menu.save(this.innerDate)

      if (this.isEmptyDate) {
        return
      }

      let datesArray = this.orderedInnerDate

      if (this.isSingleDate) {
        const [date] = this.innerDate.filter(date => !!date)

        this.innerDate[0] = date
        this.innerDate[1] = date
        datesArray = [...this.innerDate]
      }
      const [start, end] = datesArray
      const startDate = this.$moment.utc(start + ' 00:00:00')
      const endDate = this.$moment.utc(end + ' 00:00:00')

      const [startHour, startMinutes] = this.innerTime.start.split(':')
      startDate.add(startHour, 'hours')
      startDate.add(startMinutes, 'minutes')

      const [endHour, endMinutes] = this.innerTime.end.split(':')
      endDate.add(endHour, 'hours')
      endDate.add(endMinutes, 'minutes')
      endDate.add(59, 'seconds')
      if (this.dateRangeString) {
        this.$emit('input', `${startDate.format(this.returnValueFormat)}|${endDate.format(this.returnValueFormat)}`)
      } else {
        this.$emit('input', {
          start: startDate.format(this.returnValueFormat),
          end: endDate.format(this.returnValueFormat),
        })
      }
    },
  },
}
</script>

<style lang="scss">
.ui-date-picker-menu {
  border-radius: 16px;
  box-shadow: 0px 20px 30px rgba(0, 0, 0, 0.08);
}
.ui-text-field-disabled {
  opacity: 0.6;
}

.v-date-picker-table .v-btn.v-btn--disabled {
  color: var(--v-black-lighten2) !important;
}

.date-period-select .multiselect__content .multiselect__element:nth-child(3n):after {
  content: '';
  display: block;
  background: #e4e4e4;
  width: calc(100% - 20px);
  margin-left: 10px;
  height: 1px;
}
</style>
